import type { LandRegistryData } from "@/types/public-data"
import PriceTrendChart from "@/components/public/price-trend-chart"

function titleCase(street: string): string {
  return street
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase())
}

function formatDate(dateString: string): string {
  const d = new Date(dateString)
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  })
}

function formatPrice(value: number): string {
  return `\u00A3${value.toLocaleString()}`
}

interface PricesCardProps {
  data: LandRegistryData
}

export default function PricesCard({ data }: PricesCardProps) {
  const recentSales = [...data.soldPrices]
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    .slice(0, 5)

  return (
    <div className="rounded-xl border border-stone bg-white p-6">
      <h3 className="font-display text-lg font-semibold text-charcoal">
        Property Prices
      </h3>

      {data.averagePrice !== null && (
        <div className="mt-3 flex items-baseline gap-2">
          <span className="text-2xl font-bold text-charcoal">
            {formatPrice(data.averagePrice)}
          </span>
          <span className="text-sm text-slate">area average</span>
        </div>
      )}

      {data.priceIndex.length > 0 && (
        <div className="mt-4">
          <PriceTrendChart data={data.priceIndex} />
        </div>
      )}

      {recentSales.length > 0 && (
        <div className="mt-4">
          <h4 className="text-sm font-medium text-charcoal">Recent Sales</h4>
          <div className="mt-2 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-stone text-left text-xs text-slate">
                  <th className="pb-2 font-medium">Street</th>
                  <th className="pb-2 font-medium">Price</th>
                  <th className="pb-2 font-medium">Date</th>
                </tr>
              </thead>
              <tbody>
                {recentSales.map((sale, i) => (
                  <tr key={`${sale.street}-${sale.date}-${i}`} className="border-b border-stone/50">
                    <td className="py-2 text-charcoal">{titleCase(sale.street)}</td>
                    <td className="py-2 font-medium text-charcoal">
                      {formatPrice(sale.price)}
                    </td>
                    <td className="py-2 text-slate">{formatDate(sale.date)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
